import { useSyncExternalStore } from 'react';

/**
 * The **visible** viewport, and how much of the layout viewport the on-screen
 * keyboard is covering.
 *
 * iOS Safari does not resize the layout viewport when the soft keyboard opens —
 * it only shrinks the *visual* viewport and slides it up. So `100vh` / `100dvh`
 * / `window.innerHeight` all keep reporting the pre-keyboard height, and a
 * `position: fixed; bottom: 0` element stays pinned to the layout-viewport
 * bottom, i.e. **behind the keyboard**. Safari then scroll-into-views the
 * focused field, dragging the whole document — the "everything shifted and I
 * can't see what I'm typing" failure.
 *
 * `window.visualViewport` is the only surface that reports the truth. ChatGPT
 * and Gemini on iOS do exactly this: measure the visual viewport and lift the
 * composer by the keyboard inset themselves, so the browser never has a reason
 * to scroll.
 *
 * `keyboardInset` is the gap between the bottom of the visible area and the
 * bottom of the layout viewport. `offsetTop` is folded in on purpose: if Safari
 * *has* already scrolled the visual viewport, a fixed element anchored to the
 * layout viewport is off by exactly that much, and this formula cancels it.
 *
 * Returns `innerHeight` / `0` when `visualViewport` is unavailable (SSR, jsdom,
 * older browsers) so callers degrade to today's behaviour rather than to zero.
 */
export interface VisualViewportInsets {
  /** Height of the area the user can actually see, in CSS px. */
  height: number;
  /** Layout-viewport px hidden behind the soft keyboard. `0` when closed. */
  keyboardInset: number;
}

const read = (): VisualViewportInsets => {
  if (typeof window === 'undefined') return { height: 0, keyboardInset: 0 };
  const vv = window.visualViewport;
  if (!vv) return { height: window.innerHeight, keyboardInset: 0 };
  return {
    height: Math.round(vv.height),
    keyboardInset: Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)),
  };
};

const SERVER_SNAPSHOT: VisualViewportInsets = { height: 0, keyboardInset: 0 };

/**
 * Cached so repeated calls return a REFERENTIALLY equal object while the
 * numbers are unchanged — `useSyncExternalStore` re-renders forever otherwise.
 * Doubles as the burst filter: iOS fires resize+scroll in floods while the
 * keyboard animates, and only a changed value should reach React.
 */
let snapshot: VisualViewportInsets = SERVER_SNAPSHOT;

function getSnapshot(): VisualViewportInsets {
  const next = read();
  if (next.height !== snapshot.height || next.keyboardInset !== snapshot.keyboardInset) snapshot = next;
  return snapshot;
}

const getServerSnapshot = (): VisualViewportInsets => SERVER_SNAPSHOT;

const NOOP = () => {};
const noopSubscribe = () => NOOP;

/**
 * Re-reads scheduled after a focus, in ms.
 *
 * iOS raises the keyboard AFTER the focus event, and the composer autofocuses
 * when the sheet opens — so the first measurement is of the PRE-keyboard
 * viewport. If the `resize` that follows is missed (or lands while the sheet is
 * still mounting), the sheet stays sized for a viewport that no longer exists:
 * on the device, 716px of sheet in a 448px window, i.e. only the header and
 * product row visible above the keyboard.
 *
 * Re-reading a few times after focus is free — `getSnapshot` is cached, so a
 * read that finds the same numbers does not re-render.
 */
const POST_FOCUS_REREADS_MS = [150, 350, 700];

function subscribe(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') return NOOP;
  const vv = window.visualViewport;
  const timers: number[] = [];
  const onFocusIn = () => {
    for (const delay of POST_FOCUS_REREADS_MS) timers.push(window.setTimeout(onStoreChange, delay));
  };
  vv?.addEventListener('resize', onStoreChange);
  vv?.addEventListener('scroll', onStoreChange);
  window.addEventListener('resize', onStoreChange);
  window.addEventListener('orientationchange', onStoreChange);
  window.addEventListener('focusin', onFocusIn);
  return () => {
    for (const id of timers) window.clearTimeout(id);
    vv?.removeEventListener('resize', onStoreChange);
    vv?.removeEventListener('scroll', onStoreChange);
    window.removeEventListener('resize', onStoreChange);
    window.removeEventListener('orientationchange', onStoreChange);
    window.removeEventListener('focusin', onFocusIn);
  };
}

/**
 * `useSyncExternalStore`, NOT `useState` + `useEffect`.
 *
 * The effect version seeded its state when the WIDGET mounted — with the page,
 * long before the sheet is opened — and only re-measured after the first paint.
 * So opening the bubble while the keyboard was up painted one frame at the
 * stale insets (full height, `bottom: 0`, i.e. behind the keyboard) and then
 * corrected, with the sheet's `bottom`/`height` transition animating the
 * correction: the sheet visibly flew up and settled back. Reading at RENDER
 * time means the first painted frame is already right, so there is nothing to
 * correct and nothing to animate.
 */
export function useVisualViewportInsets(enabled = true): VisualViewportInsets {
  return useSyncExternalStore(enabled ? subscribe : noopSubscribe, getSnapshot, getServerSnapshot);
}
